import React, { useEffect } from "react";
import AOS from "aos";
import SEO from "../components/SEO";
import { motion } from "framer-motion";

const FeeStructurePage = () => {
    useEffect(() => {
        AOS.init({ duration: 800, once: true });
    }, []);

    // Ensure the page scrolls to top whenever this page is navigated to or the id changes
    useEffect(() => {
        try {
            if (typeof window !== 'undefined' && window?.scrollTo) {
                // instant jump to top to avoid flashing at previous scroll position
                window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
            }
        } catch (e) {
            // fallback: simple assignment
            try {
                window.scrollTo(0, 0);
            } catch (err) {
                // ignore in environments without window
            }
        }
    }, []);

    return (
        <section className="bg-gray-100 min-h-screen pt-12">
            <SEO 
                title="Fee Structure – Africa Research University" 
                description="Comprehensive fee structure for diploma, undergraduate, and postgraduate programs at ARU."
                url="/fee-structure"
                breadcrumbs={[
                    { name: 'Home', url: 'https://africaresearchuniversity.com' },
                    { name: 'About', url: 'https://africaresearchuniversity.com/about' },
                    { name: 'Fee Structure', url: 'https://africaresearchuniversity.com/fee-structure' }
                ]}
            />

            <div className="container mx-auto px-6 py-16">
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    className="max-w-6xl mx-auto bg-white rounded-2xl shadow-sm border p-6 sm:p-10"
                >
                    <h2
                        className="text-3xl font-bold text-center text-green-700 mb-4"
                        data-aos="fade-up"
                    >
                        Fee Structure
                    </h2>
                    <p className="text-center text-gray-600 mb-10" data-aos="fade-up">
                        January 2026
                    </p>

                    {/* Bachelor Degree Programs */}
                    <div className="mb-12" data-aos="fade-up">
                        <h3 className="text-2xl font-semibold text-green-700 mb-6">
                            Bachelor Degree Programmes
                        </h3>
                        
                        <div className="overflow-x-auto mb-6">
                            <table className="min-w-full border border-gray-200 rounded-lg text-sm">
                                <thead className="bg-green-600 text-white">
                                    <tr>
                                        <th className="py-3 px-4 text-left">Qualification</th>
                                        <th className="py-3 px-4 text-left">Duration</th>
                                        <th className="py-3 px-4 text-left">Number of Modules</th>
                                        <th className="py-3 px-4 text-left">Cost Per Semester</th>
                                        <th className="py-3 px-4 text-left">Exam Fee</th>
                                        <th className="py-3 px-4 text-left">Total Per Semester</th>
                                        <th className="py-3 px-4 text-left">Total Program Cost</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    <tr>
                                        <td className="py-3 px-4 font-semibold">Bachelors</td>
                                        <td className="py-3 px-4">4 Years / 8 Semesters</td>
                                        <td className="py-3 px-4">4</td>
                                        <td className="py-3 px-4">K8,750.00</td>
                                        <td className="py-3 px-4">K800.00</td>
                                        <td className="py-3 px-4">K9,550.00</td>
                                        <td className="py-3 px-4">K76,450.00</td>
                                    </tr>
                                    <tr>
                                        <td className="py-3 px-4 font-semibold">Bachelors</td>
                                        <td className="py-3 px-4">2 Years / 4 Semesters</td>
                                        <td className="py-3 px-4">4</td>
                                        <td className="py-3 px-4">K8,750.00</td>
                                        <td className="py-3 px-4">K800.00</td>
                                        <td className="py-3 px-4">K9,550.00</td>
                                        <td className="py-3 px-4">K38,200.00</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <h4 className="text-lg font-semibold text-gray-800 mb-4">Payment Plan</h4>
                        <div className="overflow-x-auto mb-4">
                            <table className="min-w-full border border-gray-200 rounded-lg text-sm">
                                <thead className="bg-gray-100">
                                    <tr>
                                        <th className="py-3 px-4 text-left">Description</th>
                                        <th className="py-3 px-4 text-right">Amount</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    <tr>
                                        <td className="py-3 px-4">Initial Deposit / Registration Fee</td>
                                        <td className="py-3 px-4 text-right">K2,000.00</td>
                                    </tr>
                                    <tr>
                                        <td className="py-3 px-4">Month 2</td>
                                        <td className="py-3 px-4 text-right">K1,350.00</td>
                                    </tr>
                                    <tr>
                                        <td className="py-3 px-4">Month 3</td>
                                        <td className="py-3 px-4 text-right">K1,350.00</td>
                                    </tr>
                                    <tr>
                                        <td className="py-3 px-4">Month 4</td>
                                        <td className="py-3 px-4 text-right">K1,350.00</td>
                                    </tr>
                                    <tr>
                                        <td className="py-3 px-4">Month 5</td>
                                        <td className="py-3 px-4 text-right">K1,350.00</td>
                                    </tr>
                                    <tr>
                                        <td className="py-3 px-4">Month 6</td>
                                        <td className="py-3 px-4 text-right">K1,350.00</td>
                                    </tr>
                                    <tr className="bg-gray-50 font-semibold">
                                        <td className="py-3 px-4">Total Cost Per Semester</td>
                                        <td className="py-3 px-4 text-right">K9,550.00</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded mb-6">
                            <p className="text-gray-800 font-semibold">
                                7.5% Discount will be given on a full programme/semester
                            </p>
                        </div>

                        <h4 className="text-lg font-semibold text-gray-800 mb-4">Other Fees</h4>
                        <div className="overflow-x-auto">
                            <table className="min-w-full border border-gray-200 rounded-lg text-sm">
                                <thead className="bg-gray-100">
                                    <tr>
                                        <th className="py-3 px-4 text-left">Fee Type</th>
                                        <th className="py-3 px-4 text-right">Price</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    <tr>
                                        <td className="py-3 px-4">Bachelors Degree Research Project Fee</td>
                                        <td className="py-3 px-4 text-right">K2,500.00</td>
                                    </tr>
                                    <tr>
                                        <td className="py-3 px-4">Exam Rewriting/Subject</td>
                                        <td className="py-3 px-4 text-right">K1,000.00</td>
                                    </tr>
                                    <tr>
                                        <td className="py-3 px-4">Re-marking/Subject</td>
                                        <td className="py-3 px-4 text-right">K200.00</td>
                                    </tr>
                                    <tr>
                                        <td className="py-3 px-4">Printing fee per book (Excludes courier fees)</td>
                                        <td className="py-3 px-4 text-right">K500.00</td>
                                    </tr>
                                    <tr>
                                        <td className="py-3 px-4">Exemption & Credit Transfer/Subject</td>
                                        <td className="py-3 px-4 text-right">K500.00</td>
                                    </tr>
                                    <tr>
                                        <td className="py-3 px-4">Change of Program fee</td>
                                        <td className="py-3 px-4 text-right">K240.00</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Master's Programs */}
                    <div className="mb-12" data-aos="fade-up">
                        <h3 className="text-2xl font-semibold text-green-700 mb-6">
                            Tutored Master's Degree Programmes
                        </h3>

                        <div className="overflow-x-auto mb-4">
                            <table className="min-w-full border border-gray-200 rounded-lg text-sm">
                                <thead className="bg-green-600 text-white">
                                    <tr>
                                        <th className="py-3 px-4 text-left">Description</th>
                                        <th className="py-3 px-4 text-right">Tuition Fee</th>
                                        <th className="py-3 px-4 text-right">Exam Fees</th>
                                        <th className="py-3 px-4 text-right">Total</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    <tr>
                                        <td className="py-3 px-4">Month 1</td>
                                        <td className="py-3 px-4 text-right">K2,000.00</td>
                                        <td className="py-3 px-4 text-right">-</td>
                                        <td className="py-3 px-4 text-right">K2,000.00</td>
                                    </tr>
                                    <tr>
                                        <td className="py-3 px-4">Month 2-13</td>
                                        <td className="py-3 px-4 text-right">K1,745.00/month</td>
                                        <td className="py-3 px-4 text-right">-</td>
                                        <td className="py-3 px-4 text-right">K1,745.00/month</td>
                                    </tr>
                                    <tr>
                                        <td className="py-3 px-4">Month 14</td>
                                        <td className="py-3 px-4 text-right">K1,745.00</td>
                                        <td className="py-3 px-4 text-right">K800.00</td>
                                        <td className="py-3 px-4 text-right">K2,545.00</td>
                                    </tr>
                                    <tr className="bg-gray-50 font-semibold">
                                        <td className="py-3 px-4">Total Program</td>
                                        <td className="py-3 px-4 text-right">K40,000.00</td>
                                        <td className="py-3 px-4 text-right">K800.00</td>
                                        <td className="py-3 px-4 text-right">K40,800.00</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                            <p className="text-gray-800 font-semibold">
                                7.5% Discount will be given on a full programme/semester
                            </p>
                        </div>
                    </div>

                    {/* Master's by Research */}
                    <div className="mb-12" data-aos="fade-up">
                        <h3 className="text-2xl font-semibold text-green-700 mb-6">
                            Masters by Research
                        </h3>

                        <div className="overflow-x-auto mb-4">
                            <table className="min-w-full border border-gray-200 rounded-lg text-sm">
                                <thead className="bg-green-600 text-white">
                                    <tr>
                                        <th className="py-3 px-4 text-left">Description</th>
                                        <th className="py-3 px-4 text-right">Amount</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    <tr>
                                        <td className="py-3 px-4">Initial Deposit</td>
                                        <td className="py-3 px-4 text-right">K2,400.00</td>
                                    </tr>
                                    <tr>
                                        <td className="py-3 px-4">Instalments (Months 2-6)</td>
                                        <td className="py-3 px-4 text-right">K2,100.00/month</td>
                                    </tr>
                                    <tr>
                                        <td className="py-3 px-4">Total Tuition</td>
                                        <td className="py-3 px-4 text-right">K40,200.00</td>
                                    </tr>
                                    <tr>
                                        <td className="py-3 px-4">Journal Publication Fee</td>
                                        <td className="py-3 px-4 text-right">K1,800.00</td>
                                    </tr>
                                    <tr>
                                        <td className="py-3 px-4">Exam Fee</td>
                                        <td className="py-3 px-4 text-right">K2,000.00</td>
                                    </tr>
                                    <tr className="bg-gray-50 font-semibold">
                                        <td className="py-3 px-4">Total Program Cost</td>
                                        <td className="py-3 px-4 text-right">K44,000.00</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Doctorate (PhD) */}
                    <div className="mb-12" data-aos="fade-up">
                        <h3 className="text-2xl font-semibold text-green-700 mb-6">
                            Doctorate (PhD)
                        </h3>

                        <div className="overflow-x-auto mb-4">
                            <table className="min-w-full border border-gray-200 rounded-lg text-sm">
                                <thead className="bg-green-600 text-white">
                                    <tr>
                                        <th className="py-3 px-4 text-left">Description</th>
                                        <th className="py-3 px-4 text-right">Year 1</th>
                                        <th className="py-3 px-4 text-right">Year 2</th>
                                        <th className="py-3 px-4 text-right">Year 3</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    <tr>
                                        <td className="py-3 px-4">Month 1</td>
                                        <td className="py-3 px-4 text-right">K6,000.00</td>
                                        <td className="py-3 px-4 text-right">K6,000.00</td>
                                        <td className="py-3 px-4 text-right">K6,000.00</td>
                                    </tr>
                                    <tr>
                                        <td className="py-3 px-4">Months 2-12</td>
                                        <td className="py-3 px-4 text-right">K1,460.00/month</td>
                                        <td className="py-3 px-4 text-right">K1,460.00/month</td>
                                        <td className="py-3 px-4 text-right">K1,460.00/month</td>
                                    </tr>
                                    <tr className="bg-gray-50 font-semibold">
                                        <td className="py-3 px-4">Total Cost Per Year</td>
                                        <td className="py-3 px-4 text-right">K26,600.00</td>
                                        <td className="py-3 px-4 text-right">K26,600.00</td>
                                        <td className="py-3 px-4 text-right">K26,600.00</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded mb-4">
                            <p className="text-gray-800 font-semibold">
                                Total Cost of Qualification (Full Program): K79,800.00
                            </p>
                        </div>

                        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                            <p className="text-gray-800 font-semibold">
                                7.5% Discount will be given on a full programme/semester
                            </p>
                        </div>
                    </div>

                    {/* Payment Options */}
                    <div className="mb-8" data-aos="fade-up">
                        <h3 className="text-2xl font-semibold text-green-700 mb-6">
                            Direct Payment Options
                        </h3>

                        <div className="grid md:grid-cols-2 gap-6">
                            {/* Bank Accounts */}
                            <div className="bg-gray-50 p-6 rounded-lg border">
                                <h4 className="font-semibold text-lg text-gray-800 mb-4">Bank Accounts</h4>
                                
                                <div className="mb-4">
                                    <p className="font-semibold text-green-700">FNB Zambia</p>
                                    <p className="text-sm">Account: 625 30 283 85</p>
                                    <p className="text-sm">Branch: Makeni</p>
                                    <p className="text-sm">Branch Code: 260 016</p>
                                    <p className="text-sm">Swift Code: FIRNZMLX</p>
                                </div>

                                <div className="mb-4">
                                    <p className="font-semibold text-green-700">FNB South Africa</p>
                                    <p className="text-sm">Account: 628 355 78587</p>
                                    <p className="text-sm">Branch: Business Branch</p>
                                    <p className="text-sm">Branch Code: 250 655</p>
                                    <p className="text-sm">Swift Code: FIRNZAJJ</p>
                                </div>

                                <div className="mb-4">
                                    <p className="font-semibold text-green-700">ZANACO</p>
                                    <p className="text-sm">Account: 531 550 650 0181</p>
                                    <p className="text-sm">Africa Research University</p>
                                    <p className="text-sm">Cairo Road Business Centre</p>
                                </div>

                                <div className="mb-4">
                                    <p className="font-semibold text-green-700">ATLAS MARA</p>
                                    <p className="text-sm">Account: 032 002 058 4018</p>
                                    <p className="text-sm">Branch: Downtown</p>
                                    <p className="text-sm">Branch Code: 110 032</p>
                                </div>
                            </div>

                            {/* Mobile Money */}
                            <div className="bg-gray-50 p-6 rounded-lg border">
                                <h4 className="font-semibold text-lg text-gray-800 mb-4">Mobile Money</h4>
                                
                                <div className="mb-4">
                                    <p className="font-semibold text-green-700">MUKURU</p>
                                    <p className="text-sm">+260 973 684 500</p>
                                    <p className="text-sm">+260 768 899 404</p>
                                    <p className="text-sm">Cecilia Chibawe Chisenga</p>
                                </div>

                                <div className="mb-4">
                                    <p className="font-semibold text-green-700">MTN MONEY</p>
                                    <p className="text-sm">+260 962 542 357</p>
                                    <p className="text-sm">Cecilia Chisenga</p>
                                </div>

                                <div className="mb-4">
                                    <p className="font-semibold text-green-700">AIRTEL MONEY</p>
                                    <p className="text-sm">+260 973 684 500</p>
                                    <p className="text-sm">Johnny Likando</p>
                                </div>

                                <div className="mt-6 pt-6 border-t">
                                    <p className="font-semibold text-green-700 mb-2">Online Payment</p>
                                    <p className="text-sm">
                                        Pay via D.P.O by visiting the Africa Research University website, 
                                        navigate to Online Payment, select E-Payment, and choose the relevant 
                                        fee or service you are paying for.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Important Note */}
                    <div
                        className="mt-8 bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded"
                        data-aos="fade-up"
                    >
                        <p className="text-gray-800 italic">
                            <strong>Important:</strong> Students are encouraged to make payments directly 
                            into the institution's official bank accounts or through our mobile money services. 
                            When making a payment, it is essential to include the student's number and, 
                            if possible, their name on the deposit slip to ensure proper tracking and 
                            allocation of funds.
                        </p>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default FeeStructurePage;
